<template>
  <div class="mt-admin">
    <div class="mta-page-head">
      <h1>All users</h1>
      <span class="mta-spacer"></span>
      <span v-if="refreshedAt" class="mta-muted" style="font-size:12px">Data as of {{ refreshedAt }} UTC</span>
    </div>
    <p class="mta-page-sub">Every account with its tool usage, purchases and revenue. Filter by package, status, Customer Manager or a saved segment, then sort any column.</p>

    <div class="mta-toolbar">
      <input class="mta-ctl mta-ctl--grow" v-model="searchInput" placeholder="Search name, email or user ID" @keyup.enter="applySearch" />
      <button type="button" class="mta-btn mta-btn--secondary mta-btn--sm" @click="applySearch">Search</button>
      <button type="button" class="mta-btn mta-btn--secondary mta-btn--sm" :disabled="exporting" @click="exportCsv">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16"/></svg>
        {{ exporting ? 'Exporting…' : 'Export CSV (filtered)' }}
      </button>
    </div>
    <div class="mta-filters">
      <label class="mta-fld">Package
        <select :value="filters.package" @change="setFilter('package', $event.target.value)">
          <option value="all">All</option>
          <option v-for="p in packages" :key="p.key" :value="p.key">{{ p.label }}</option>
        </select>
      </label>
      <label class="mta-fld">Status
        <select :value="filters.status" @change="setFilter('status', $event.target.value)">
          <option value="all">All</option>
          <option v-for="s in statuses" :key="s" :value="s">{{ s }}</option>
        </select>
      </label>
      <label class="mta-fld">Customer Manager
        <select :value="filters.cm" @change="setFilter('cm', $event.target.value)">
          <option value="all">All</option>
          <option v-for="m in managers" :key="m.key" :value="m.key">{{ m.name }}</option>
        </select>
      </label>
      <label class="mta-fld">Segment
        <select :value="filters.segment" @change="setFilter('segment', $event.target.value)">
          <option value="all">All</option>
          <option v-for="s in segments" :key="s.key" :value="s.key">{{ s.label }}</option>
        </select>
      </label>
    </div>

    <div v-if="error" class="mta-error">{{ error }}</div>

    <div class="mta-stat-row">
      <span class="mta-muted">{{ num(stats.users) }} users</span>
      <span>MRR <b>{{ usd(stats.mrr) }}</b></span>
      <span>Revenue <b>{{ usd(stats.revenue) }}</b></span>
      <span>POP runs <b>{{ num(stats.runs) }}</b></span>
      <span>Avg runs / day ({{ windowDays }}d) <b>{{ (stats.runsPerDay || 0).toFixed(1) }}</b></span>
      <span>Credits used <b>{{ num(stats.credits) }}</b></span>
      <span>Avg revenue / user <b>{{ usd(stats.revenuePerUser) }}</b></span>
      <span class="mta-spacer"></span>
      <span class="mta-muted" style="font-size:12px">Click a column to sort. All-time figures.</span>
    </div>

    <div class="mta-card mta-tblwrap">
      <table class="mta-tbl">
        <thead>
          <tr>
            <th v-for="col in columns" :key="col.key" class="mta-sortable" :class="[col.right ? 'mta-r' : '', sort.field === col.key ? 'mta-sorted' : '']"
              @click="sortBy(col.key)">{{ col.label }}{{ sortMark(col.key) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading"><td :colspan="columns.length" class="mta-empty">Loading…</td></tr>
          <tr v-else-if="!records.length"><td :colspan="columns.length" class="mta-empty">No users match these filters.</td></tr>
          <template v-else>
          <tr v-for="u in records" :key="u.user_id">
            <td>
              <a :href="userAccountsHref(u.email)" target="_blank" rel="noopener"><b>{{ u.name || u.email }}</b></a>
              <span class="mta-sub">{{ u.email }} · #{{ u.user_id }}</span>
            </td>
            <td>{{ u.package_label }}<span v-if="u.cm" class="mta-sub">CM: {{ managerName(u.cm) }}</span></td>
            <td class="mta-r mta-num">
              <template v-if="u.mrr"><b>{{ usd(u.mrr) }}</b>
                <span v-if="u.seats > 1" class="mta-sub">{{ usd(u.plan_price) }} × {{ u.seats }} seats</span>
              </template>
              <span v-else class="mta-muted">$0</span>
            </td>
            <td><span class="mta-badge" :class="'mta-badge--' + statusTone(u.status)">{{ u.status }}</span></td>
            <td>{{ dateOnly(u.joined) }}<span class="mta-sub">{{ u.months }} mo</span></td>
            <td>{{ dateOnly(u.last_active) || '–' }}<span v-if="u.risk" class="mta-sub"><span class="mta-badge mta-badge--warn" style="font-size:10px">{{ u.risk }}</span></span></td>
            <td>{{ dateOnly(u.cancelled) || '–' }}<span v-if="u.cancel_reason" class="mta-sub">{{ u.cancel_reason }}</span></td>
            <td>{{ dateOnly(u.last_purchase) || 'never' }}</td>
            <td>{{ u.source }}<span class="mta-sub">{{ u.country }}<span v-if="u.seats > 1"> · {{ u.seats }} seats</span></span></td>
            <td class="mta-r mta-num">{{ num(u.runs) }}</td>
            <td class="mta-r mta-num"><b>{{ (u.runs_per_day || 0).toFixed(1) }}</b></td>
            <td class="mta-r mta-num">{{ num(u.runs_90d) }}</td>
            <td class="mta-r mta-num">{{ num(u.aiw) }}</td>
            <td class="mta-r mta-num">{{ num(u.kw) }}</td>
            <td class="mta-r mta-num">{{ num(u.wd) }}</td>
            <td class="mta-r mta-num">{{ num(u.aiv) }}</td>
            <td class="mta-r mta-num">{{ num(u.credits_used) }}</td>
            <td class="mta-r mta-num">{{ num(u.orders) }}</td>
            <td class="mta-r mta-num">{{ usd(u.sub_rev) }}</td>
            <td class="mta-r mta-num">{{ usd(u.credit_rev) }}</td>
            <td class="mta-r mta-num">{{ usd(u.wg_rev) }}</td>
            <td class="mta-r mta-num">{{ usd(u.pa_rev) }}</td>
            <td class="mta-r mta-num">{{ usd(u.api_rev) }}</td>
            <td class="mta-r mta-num"><b>{{ usd(u.total_rev) }}</b></td>
            <td class="mta-r mta-num">{{ usd(u.aov) }}</td>
            <td class="mta-r mta-num">{{ usd(u.ltv) }}</td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-if="pageCount > 1" class="mta-pager">
      <button type="button" :disabled="page === 1" @click="setPage(page - 1)">‹</button>
      <template v-for="(item, idx) in pagerItems">
        <span v-if="item === '…'" :key="'dots-' + idx" class="mta-pager__dots">…</span>
        <button v-else :key="'p-' + item" type="button" :class="{ 'mta-on': item === page }" @click="setPage(item)">{{ item }}</button>
      </template>
      <button type="button" :disabled="page === pageCount" @click="setPage(page + 1)">›</button>
    </div>
  </div>
</template>

<script>
const PER_PAGE = 25
const WINDOW_DAYS = 12  // helpers/admin_metrics.RUNS_PER_DAY_WINDOW_DAYS

const COLUMNS = [
  { key: 'email', label: 'User' },
  { key: 'package_key', label: 'Package' },
  { key: 'mrr', label: 'Pays / month', right: true },
  { key: 'status', label: 'Status' },
  { key: 'joined', label: 'Joined' },
  { key: 'last_active', label: 'Last active' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'last_purchase', label: 'Last purchase' },
  { key: 'source', label: 'Source' },
  { key: 'runs', label: 'POP runs', right: true },
  { key: 'runs_per_day', label: 'Runs / day (' + WINDOW_DAYS + 'd)', right: true },
  { key: 'runs_90d', label: 'Runs (90d, fair use)', right: true },
  { key: 'aiw', label: 'AI articles', right: true },
  { key: 'kw', label: 'KW reports', right: true },
  { key: 'wd', label: 'Watchdog pages', right: true },
  { key: 'aiv', label: 'AI Vis. trackers', right: true },
  { key: 'credits_used', label: 'Credits used', right: true },
  { key: 'orders', label: 'Orders', right: true },
  { key: 'sub_rev', label: 'Subscription $', right: true },
  { key: 'credit_rev', label: 'Credits $', right: true },
  { key: 'wg_rev', label: 'White Glove $', right: true },
  { key: 'pa_rev', label: 'Authority $', right: true },
  { key: 'api_rev', label: 'API $', right: true },
  { key: 'total_rev', label: 'Total revenue', right: true },
  { key: 'aov', label: 'Avg order', right: true },
  { key: 'ltv', label: 'Est. LTV', right: true },
]

/**
 * Admin > Metrics > All users (design "POP Admin - User Metrics.html"). The rows are the stored
 * per-user metrics (GET /api/admin/metrics/users/); the server filters, sorts and paginates, and the
 * CSV export is the same listing without pagination.
 */
export default {
  name: 'AdminMetricsAllUsers',
  data () {
    return {
      loading: false,
      exporting: false,
      error: '',
      records: [],
      total: 0,
      stats: {},
      managers: [],
      packages: [],
      segments: [],
      statuses: ['Active', 'Trial', 'Inactive', 'Churned'],
      refreshedAt: '',
      page: 1,
      searchInput: '',
      search: '',
      sort: { field: 'total_rev', order: 'desc' },
      filters: { package: 'all', status: 'all', cm: 'all', segment: 'all' },
      columns: COLUMNS,
      windowDays: WINDOW_DAYS,
      requestId: 0,
    }
  },
  computed: {
    pageCount () {
      return Math.max(1, Math.ceil(this.total / PER_PAGE))
    },
    pagerItems () {
      const n = this.pageCount
      const current = this.page
      if (n <= 9) return Array.from({ length: n }, (_, i) => i + 1)
      const items = [1]
      const from = Math.max(2, current - 2)
      const to = Math.min(n - 1, current + 2)
      if (from > 2) items.push('…')
      for (let i = from; i <= to; i++) items.push(i)
      if (to < n - 1) items.push('…')
      items.push(n)
      return items
    },
  },
  created () {
    this.load()
  },
  methods: {
    params () {
      const values = {
        page: this.page,
        perPage: PER_PAGE,
        sortField: this.sort.field,
        sortOrder: this.sort.order,
        filterText: this.search,
        ...this.filters,
      }
      const params = new URLSearchParams()
      Object.keys(values).forEach((key) => {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') params.set(key, values[key])
      })
      return params.toString()
    },
    load () {
      const id = ++this.requestId
      this.loading = true
      this.error = ''
      return this.$store.dispatch('getAdminMetricsUsers', { params: this.params() })
        .then((response) => {
          if (id !== this.requestId) return
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') throw new Error('listing failed')
          this.records = data.records || []
          this.total = data.total || 0
          this.stats = data.stats || {}
          this.managers = data.managers || []
          this.packages = data.packages || []
          this.segments = data.segments || []
          this.refreshedAt = data.refreshedAt || ''
        })
        .catch(() => {
          if (id !== this.requestId) return
          this.records = []
          this.total = 0
          this.error = 'We could not load the users. Try again in a moment.'
        })
        .finally(() => {
          if (id === this.requestId) this.loading = false
        })
    },
    applySearch () {
      this.search = (this.searchInput || '').trim()
      this.page = 1
      return this.load()
    },
    setFilter (key, value) {
      this.$set(this.filters, key, value)
      this.page = 1
      return this.load()
    },
    setPage (value) {
      const page = Math.min(Math.max(value, 1), this.pageCount)
      if (page === this.page) return
      this.page = page
      this.load()
    },
    sortBy (field) {
      if (this.sort.field === field) {
        this.sort.order = this.sort.order === 'desc' ? 'asc' : 'desc'
      } else {
        this.sort = { field, order: 'desc' }
      }
      this.page = 1
      return this.load()
    },
    sortMark (field) {
      if (this.sort.field !== field) return ''
      return this.sort.order === 'desc' ? ' ↓' : ' ↑'
    },
    exportCsv () {
      this.exporting = true
      return this.$store.dispatch('exportAdminMetricsUsers', { params: this.params() })
        .then((response) => {
          const blob = new Blob([response.data], { type: 'text/csv' })
          const link = document.createElement('a')
          const url = window.URL.createObjectURL(blob)
          link.download = 'pop-users.csv'
          link.href = url
          link.style.display = 'none'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
        .catch(() => {
          this.$notify({ group: 'info', type: 'error', text: 'The export failed. Try again in a moment.' })
        })
        .finally(() => {
          this.exporting = false
        })
    },
    managerName (key) {
      const manager = this.managers.find((m) => m.key === key)
      return manager ? manager.name : key
    },
    statusTone (status) {
      return { Active: 'suc', Trial: 'info', Inactive: 'warn', Churned: 'dan' }[status] || 'grey'
    },
    dateOnly (value) {
      return value ? String(value).slice(0, 10) : ''
    },
    num (value) {
      return Number(value || 0).toLocaleString('en-US')
    },
    usd (value) {
      return '$' + Math.round(Number(value || 0)).toLocaleString('en-US')
    },
    /** Admin > User Accounts with the customer already searched (new tab). */
    userAccountsHref (email) {
      const me = this.$store.state.user || {}
      return this.$router.resolve({ path: `/admin/${me.id}`, query: { activeTab: 'userAccounts', search: email } }).href
    },
  },
}
</script>

<style lang="scss">
@import './metricsAdmin';
</style>
